/*
 * Tema de la propiedad (DESIGN.md §2 "Color principal" y §9 "Regla de personalización").
 * Una propiedad solo cambia --primary; de él se derivan hover (−12 %), pressed (−25 %),
 * soft (12 % sobre blanco) y soft-text. Si --primary no alcanza 4,5:1 sobre blanco se
 * oscurece automáticamente hasta alcanzarlo, manteniendo la relación con sus derivados.
 *
 * Interpretación: "oscurecido un X %" = mezcla de X % hacia negro en el espacio perceptual
 * OKLab (luminosidad y croma escalan juntos, el tono se conserva); reproduce los derivados
 * del bundle (#175449 / #113F37). No hay dependencia: conversiones sRGB ↔ OKLCH propias.
 */

export const DEFAULT_PRIMARY = '#1E6B5E'
export const MIN_CONTRAST = 4.5
const WHITE = '#FFFFFF'

export interface ThemeVars {
  '--primary': string
  '--primary-hover': string
  '--primary-pressed': string
  '--primary-soft': string
  '--primary-soft-text': string
  '--focus-ring': string
}

export interface PropertyTheme {
  vars: ThemeVars
  /** Color recibido, normalizado a #RRGGBB */
  requested: string
  /** true si hubo que oscurecer el color para alcanzar 4,5:1 sobre blanco */
  corrected: boolean
  contrastOnWhite: number
}

type RGB = [number, number, number] // 0–1
type OKLCH = { l: number; c: number; h: number }

const HEX_RE = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i

export function parseHex(input: string): RGB | null {
  const m = HEX_RE.exec(input.trim())
  if (!m) return null
  let hex = m[1] ?? ''
  if (hex.length === 3) hex = [...hex].map((ch) => ch + ch).join('')
  const n = parseInt(hex, 16)
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255]
}

export function toHex([r, g, b]: RGB): string {
  const c = (v: number) =>
    Math.round(Math.min(1, Math.max(0, v)) * 255)
      .toString(16)
      .padStart(2, '0')
  return `#${c(r)}${c(g)}${c(b)}`.toUpperCase()
}

const srgbToLinear = (v: number) => (v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4)
const linearToSrgb = (v: number) => (v <= 0.0031308 ? v * 12.92 : 1.055 * v ** (1 / 2.4) - 0.055)

export function relativeLuminance([r, g, b]: RGB): number {
  return 0.2126 * srgbToLinear(r) + 0.7152 * srgbToLinear(g) + 0.0722 * srgbToLinear(b)
}

/** Ratio de contraste WCAG 2.x entre dos colores hex. */
export function contrastRatio(a: string, b: string): number {
  const ca = parseHex(a)
  const cb = parseHex(b)
  if (!ca || !cb) return 1
  const la = relativeLuminance(ca)
  const lb = relativeLuminance(cb)
  const [hi, lo] = la > lb ? [la, lb] : [lb, la]
  return (hi + 0.05) / (lo + 0.05)
}

function rgbToOklch([r, g, b]: RGB): OKLCH {
  const lr = srgbToLinear(r)
  const lg = srgbToLinear(g)
  const lb = srgbToLinear(b)
  const l_ = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb)
  const m_ = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb)
  const s_ = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb)
  const L = 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_
  const A = 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_
  const B = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_
  const c = Math.hypot(A, B)
  const h = ((Math.atan2(B, A) * 180) / Math.PI + 360) % 360
  return { l: L, c, h }
}

function oklchToRgbUnclamped({ l, c, h }: OKLCH): RGB {
  const A = c * Math.cos((h * Math.PI) / 180)
  const B = c * Math.sin((h * Math.PI) / 180)
  const l_ = (l + 0.3963377774 * A + 0.2158037573 * B) ** 3
  const m_ = (l - 0.1055613458 * A - 0.0638541728 * B) ** 3
  const s_ = (l - 0.0894841775 * A - 1.291485548 * B) ** 3
  const lr = 4.0767416621 * l_ - 3.3077115913 * m_ + 0.2309699292 * s_
  const lg = -1.2684380046 * l_ + 2.6097574011 * m_ - 0.3413193965 * s_
  const lb = -0.0041960863 * l_ - 0.7034186147 * m_ + 1.707614701 * s_
  return [linearToSrgb(lr), linearToSrgb(lg), linearToSrgb(lb)]
}

const inGamut = (rgb: RGB) => rgb.every((v) => v >= -0.0005 && v <= 1.0005)

/** OKLCH → sRGB reduciendo el croma hasta entrar en gamut (conserva tono y luminosidad). */
function oklchToRgb(color: OKLCH): RGB {
  let rgb = oklchToRgbUnclamped(color)
  if (inGamut(rgb)) return rgb
  let lo = 0
  let hi = color.c
  for (let i = 0; i < 20; i++) {
    const mid = (lo + hi) / 2
    rgb = oklchToRgbUnclamped({ ...color, c: mid })
    if (inGamut(rgb)) lo = mid
    else hi = mid
  }
  return oklchToRgbUnclamped({ ...color, c: lo })
}

/**
 * Oscurece un color hex un `fraction` (0–1): mezcla hacia negro en OKLab, es decir, escala
 * luminosidad y croma a la vez conservando el tono. Con 0,12 y 0,25 sobre #1E6B5E se obtiene
 * ≈ #175449 y ≈ #113F37, los derivados del bundle.
 */
export function darken(hex: string, fraction: number): string {
  const rgb = parseHex(hex)
  if (!rgb) return hex
  const ok = rgbToOklch(rgb)
  return toHex(oklchToRgb({ ...ok, l: ok.l * (1 - fraction), c: ok.c * (1 - fraction) }))
}

/** Mezcla `hex` sobre blanco al `amount` indicado (0–1) en sRGB. */
export function tintOnWhite(hex: string, amount: number): string {
  const rgb = parseHex(hex)
  if (!rgb) return hex
  return toHex(rgb.map((v) => 1 - (1 - v) * amount) as RGB)
}

/** Oscurece `hex` en pasos pequeños hasta alcanzar `min` de contraste sobre `background`. */
export function ensureContrast(hex: string, background: string, min = MIN_CONTRAST): string {
  let current = hex
  for (let i = 0; i < 60 && contrastRatio(current, background) < min; i++) {
    const next = darken(current, 0.04)
    if (next === current) break
    current = next
  }
  return current
}

/**
 * Deriva las variables de tema de una propiedad. Con entrada inválida o vacía devuelve el
 * color por defecto del sistema (la propiedad "sin personalizar" debe verse igual de acabada).
 */
export function derivePropertyTheme(primaryInput?: string | null): PropertyTheme {
  const parsed = primaryInput ? parseHex(primaryInput) : null
  const requested = parsed ? toHex(parsed) : DEFAULT_PRIMARY
  const primary = ensureContrast(requested, WHITE)
  const soft = tintOnWhite(primary, 0.12)
  const softText = ensureContrast(darken(primary, 0.12), soft)
  return {
    requested,
    corrected: primary !== requested,
    contrastOnWhite: contrastRatio(primary, WHITE),
    vars: {
      '--primary': primary,
      '--primary-hover': darken(primary, 0.12),
      '--primary-pressed': darken(primary, 0.25),
      '--primary-soft': soft,
      '--primary-soft-text': softText,
      '--focus-ring': primary,
    },
  }
}

export const THEME_VAR_NAMES = [
  '--primary',
  '--primary-hover',
  '--primary-pressed',
  '--primary-soft',
  '--primary-soft-text',
  '--focus-ring',
] as const satisfies readonly (keyof ThemeVars)[]

/** Escribe las variables en un elemento (por defecto `<html>`). */
export function applyPropertyTheme(theme: PropertyTheme, element: HTMLElement = document.documentElement): void {
  for (const name of THEME_VAR_NAMES) element.style.setProperty(name, theme.vars[name])
}

export function clearPropertyTheme(element: HTMLElement = document.documentElement): void {
  for (const name of THEME_VAR_NAMES) element.style.removeProperty(name)
}

/** Modo de texto grande (DESIGN.md §3): todos los tamaños × 1,2 mediante --text-scale. */
export function setTextLarge(enabled: boolean, element: HTMLElement = document.documentElement): void {
  if (enabled) element.setAttribute('data-text-large', '')
  else element.removeAttribute('data-text-large')
}
